/**
 * 数据传输安全改造：密钥协商模块
 * 与后端 org.jeecg.config.crypto.CryptoController 对应：
 * 1. GET  /crypto/pubkey    获取 RSA 公钥
 * 2. POST /crypto/negotiate 上报 RSA(AES 密钥)，换取 sessionId + signToken
 *
 * 前端使用 jsencrypt（RSAES-PKCS1-v1_5 填充）加密随机生成的 AES 密钥。
 * 密钥协商请求使用原生 axios 直连，不经过加解密拦截器。
 */
import axios from 'axios';
import JSEncrypt from 'jsencrypt';
import { getApiBase } from '../axios';

export const CRYPTO_PUBKEY_PATH = '/crypto/pubkey';
export const CRYPTO_NEGOTIATE_PATH = '/crypto/negotiate';

let cryptoContext = null;
// 防止并发请求触发多次密钥协商
let negotiating = null;

/**
 * 执行密钥协商（带并发互斥）
 * @returns {Promise<{sessionId:string, aesKey:string, signToken:string}>}
 */
export async function negotiateKey() {
  if (negotiating) {
    return negotiating;
  }
  negotiating = doNegotiate().finally(() => {
    negotiating = null;
  });
  return negotiating;
}

async function doNegotiate() {
  const base = getApiBase();

  // ① 获取 RSA 公钥
  const pubRes = await axios.get(`${base}${CRYPTO_PUBKEY_PATH}`);
  const publicKey = pubRes?.data?.result?.publicKey;
  if (!publicKey) {
    throw new Error(pubRes?.data?.message || '获取 RSA 公钥失败');
  }

  // ② 生成随机 AES-128 密钥（16 字节，兼容 JDK8 低版本 JCE 策略限制）
  const aesKey = generateRandomKey(16);

  // ③ 用 RSA 公钥加密 AES 密钥
  const encrypt = new JSEncrypt();
  encrypt.setPublicKey(publicKey);
  const encAesKey = encrypt.encrypt(aesKey);
  if (!encAesKey) {
    throw new Error('RSA 加密 AES 密钥失败');
  }

  // ④ 发送密钥协商请求
  const negRes = await axios.post(`${base}${CRYPTO_NEGOTIATE_PATH}`, { encAesKey });
  const result = negRes?.data?.result;
  if (!result?.sessionId) {
    throw new Error(negRes?.data?.message || '密钥协商失败');
  }

  // ⑤ 保存协商结果
  cryptoContext = {
    sessionId: result.sessionId,
    aesKey,
    signToken: result.signToken,
  };
  return cryptoContext;
}

/**
 * 生成随机密钥（从 62 位字符表中随机取值）
 * @param {number} length 密钥长度
 * @returns {string} 随机密钥
 */
function generateRandomKey(length) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars[array[i] % chars.length];
  }
  return result;
}

export function getCryptoContext() {
  return cryptoContext;
}

export function clearCryptoContext() {
  cryptoContext = null;
}

export async function ensureNegotiated() {
  if (!cryptoContext) {
    await negotiateKey();
  }
}
